import {
  Avatar,
  Button,
  EditableField,
  FormItem,
  KeyIcon,
  Loading,
  Sidebar,
  SingleSelect,
  TextInput,
  // TODO: uncomment till the archive feature came out
  // TrashIcon,
  useToastContext,
  emailRules,
  nameRules,
  phoneRules,
  Tooltip,
} from '@trufla-technology/trumarket-react-design-system';
import {
  useGetUserByKey,
  useResetUserPassword,
  useUpdateUser,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { isNull, isUndefined, join } from 'lodash';
import { LocationsType, TeamsType, User } from '../../../types/User';
import { nameInitials } from '../../../utils';
import './style.scss';
import {
  LocalizationSelect,
  languages,
} from '../../../components/LocalizationSelect';
import { currencies, CurrencySelect } from '../../../components/CurrencySelect';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';
import { NewEditableField } from './NewEditableField';

type Props = {
  teams: TeamsType;
  locations: LocationsType;
};

type userStatues = { label: string; value: boolean };

export const UserDetailsSidebar = ({ teams, locations }: Props) => {
  const { isPermitted } = useAuthContext();
  const { key: userKey } = useParams<any>();
  const { data: user, get } = useGetUserByKey(userKey);
  const userStatues: userStatues[] = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];
  const [editableValues, setEditableValues] = useState<Partial<User>>({});

  const [userStatusObj, setUserStatusObj] = useState<userStatues>();

  useEffect(() => {
    setUserStatusObj(userStatues.find((opt) => opt.value === user?.active)!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.active]);

  useEffect(() => {
    if (user) {
      setEditableValues({
        email: user?.email,
        fullName: user?.fullName,
        phoneNumber: user?.phoneNumber,
        jobTitle: user?.jobTitle,
      });
    }
  }, [user]);

  const resetUserPassword = useResetUserPassword();

  const handleResetPassword = async () => {
    await resetUserPassword({
      key: userKey,
    });

    toastContext.success('Reset Password email has been sent successfully');
  };

  const toastContext = useToastContext();
  const updateUser = useUpdateUser();

  const onSave = async (field: keyof User, value: User[keyof User]) => {
    try {
      setEditableValues((prev) => ({ ...prev, [field]: value }));
      await updateUser(userKey, {
        [field]: isUndefined(value) || isNull(value) ? null : value,
      });
      toastContext.success('Updated');
      get();
    } catch (error) {
      // @ts-ignore - type of response on error not exist
      const errorMessage = error?.response?.data?.error.replaceAll('body.', '');
      toastContext.error(errorMessage || 'Something went wrong');
      setEditableValues((prev) => ({ ...prev, [field]: user?.[field] }));
    }
  };

  const arrSeprator = (arr: TeamsType | LocationsType) => {
    return join(
      arr?.map((item) => [item.name]),
      ', ',
    );
  };

  const handleChangeUserStatus = (selectedStatus: userStatues) => {
    setUserStatusObj(selectedStatus);
    onSave('active', selectedStatus.value);
  };

  if (!user)
    return (
      <Sidebar mode="light">
        <div className="loading-wrapper">
          <Loading />
        </div>
      </Sidebar>
    );
  return (
    <div>
      <Sidebar mode="light" width="317px">
        <div className="tru-sidebar-header">
          <Avatar size="large">{nameInitials(user?.fullName)}</Avatar>
          <h2 className="client-name">{user?.fullName}</h2>
          <SingleSelect
            //   @ts-ignore - can't be boolean on RDS cause of the checks on value
            options={userStatues}
            //   @ts-ignore - can't be boolean on RDS cause of the checks on value
            defaultValue={userStatusObj}
            showCaretUpAndDownIcon
            //   @ts-ignore - can't be boolean on RDS cause of the checks on value
            onChange={handleChangeUserStatus}
            isDisabled={!isPermitted('updateUser')}
          />
          <div className="action-icons">
            <Button
              disabled={!isPermitted('resetUserPassword')}
              onClick={handleResetPassword}
            >
              <KeyIcon
                disabled={!isPermitted('resetUserPassword')}
                width="32px"
                fill="var(--tru-grey)"
              />
            </Button>
            {/* TODO: uncomment till the archive feature came out */}
            {/* <Button>
              <TrashIcon size="extra-small" fill="var(--tru-grey)" />
            </Button> */}
          </div>
        </div>
        <div className="user-info">
          <h5>Details</h5>
          <FormItem vertical label="Name" required>
            <EditableField
              key={editableValues?.fullName}
              onSave={(val) => onSave('fullName', val)}
              initialValue={editableValues?.fullName}
              rules={[...nameRules]}
            >
              <TextInput disabled={!isPermitted('updateUser')} />
            </EditableField>
          </FormItem>

          <FormItem vertical label="Phone" required>
            <EditableField
              key={editableValues?.phoneNumber}
              onSave={(val) => onSave('phoneNumber', val)}
              initialValue={editableValues?.phoneNumber}
              rules={[...phoneRules]}
              placeHolder="-"
            >
              <TextInput disabled={!isPermitted('updateUser')} />
            </EditableField>
          </FormItem>

          <FormItem vertical label="Email" required>
            <EditableField
              key={editableValues?.email}
              onSave={(val) => onSave('email', val)}
              initialValue={editableValues?.email}
              rules={[...emailRules]}
            >
              <TextInput disabled={!isPermitted('updateUser')} />
            </EditableField>
          </FormItem>
          <FormItem vertical label="Job Title" required>
            <EditableField
              key={editableValues?.jobTitle}
              onSave={(val) => onSave('jobTitle', val)}
              initialValue={editableValues?.jobTitle}
              rules={[...nameRules]}
              placeHolder="-"
            >
              <TextInput disabled={!isPermitted('updateUser')} />
            </EditableField>
          </FormItem>
          <FormItem vertical label="Teams" required>
            <span className="team-and-location-text">
              {arrSeprator(teams?.slice(0, 3))}
              {teams?.length! > 3 ? (
                <Tooltip title={arrSeprator(teams)} position="top left">
                  <span className="link">,+{teams?.length! - 3}</span>
                </Tooltip>
              ) : null}
            </span>
          </FormItem>
          <FormItem vertical label="Branches" required>
            <span className="team-and-location-text">
              {arrSeprator(locations?.slice(0, 3))}
              {locations?.length! > 3 ? (
                <Tooltip title={arrSeprator(locations)} position="top left">
                  <span className="link">,+{locations?.length! - 3}</span>
                </Tooltip>
              ) : null}
            </span>
          </FormItem>
          <FormItem vertical label="Default Language" required>
            <NewEditableField
              hideClear
              initialValue={
                languages.find((i) => i.value === user.localization?.locale)
                  ?.label
              }
              label={
                languages.find((i) => i.value === user.localization?.locale)
                  ?.label || '-'
              }
              onChange={(val: any) => {
                const localeValue = languages.find(
                  (i) => i.value === val,
                )?.value;

                onSave('localization', {
                  locale: localeValue,
                });
              }}
            >
              <LocalizationSelect disabled={!isPermitted('updateUser')} />
            </NewEditableField>
          </FormItem>
          <FormItem vertical label="Currency" required>
            <NewEditableField
              hideClear
              initialValue={
                currencies.find((i) => i.value === user.localization?.currency)
                  ?.label || '-'
              }
              label={
                currencies.find((i) => i.value === user.localization?.currency)
                  ?.label || '-'
              }
              onChange={(val: any) => {
                const currencyValue = currencies.find(
                  (i) => i.value === val,
                )?.value;

                onSave('localization', {
                  currency: currencyValue,
                });
              }}
            >
              <CurrencySelect disabled={!isPermitted('updateUser')} />
            </NewEditableField>
          </FormItem>
        </div>
      </Sidebar>
    </div>
  );
};
